/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */

// consts and types
import {
  EMAIL_SIGNATURE_LEGAL_FONT_SIZES_DICT,
  EMAIL_SIGNATURE_WIDTH,
  EMAIL_SIGNATURE_WIDTH_WITHOUT_IMAGE,
  EMAIL_SIGNATURE_WIDTH_WITH_MEDIUM_IMAGE,
  EMAIL_SIGNATURE_WIDTH_WITH_SMALL_IMAGE,
  EM_SIG_IMAGES_SIZES_DICT,
  LEGAL_DISCLAIMER_OPTIONS,
} from '@components/EmailSignaturev2/consts';
import { EmailSignatureSizes, type EmailSignatureV2 } from '@components/EmailSignaturev2/types';
// hooks
import useNode from '@hooks/use-node';
// utils
import Emitter from '@modules/event-emitter';
import { sortBy } from 'es-toolkit';
// components
import { AnimatePresence, motion } from 'motion/react';
import { type FC, useEffect, useMemo } from 'react';

import styles from './EmailSignatureLegalDisclaimer.module.scss';

interface EmailSignatureLegalDisclaimerProps {
  emailSignature: EmailSignatureV2;
  disableAllSpecialEffects: boolean;
  showDummyBanner?: boolean;
  showDummyEnvironmentalFooter?: boolean;
}

const EmailSignatureLegalDisclaimer: FC<
  React.PropsWithChildren<EmailSignatureLegalDisclaimerProps>
> = ({
  emailSignature,
  disableAllSpecialEffects,
  showDummyBanner,
  showDummyEnvironmentalFooter,
}) => {
  const [legalDisclaimerNode, legalDisclaimerNodeRef] = useNode<HTMLTableElement>();

  const legalWidth = useMemo(() => {
    const images = emailSignature.images.filter((image) => image.imageUrl);
    if (images.length === 0) {
      return EMAIL_SIGNATURE_WIDTH_WITHOUT_IMAGE;
    } else {
      const biggestImage = sortBy(images, [
        (image) => EM_SIG_IMAGES_SIZES_DICT[image.size] * -1,
      ])[0];
      if (biggestImage.size === 'small') {
        return EMAIL_SIGNATURE_WIDTH_WITH_SMALL_IMAGE;
      } else if (biggestImage.size === 'medium') {
        return EMAIL_SIGNATURE_WIDTH_WITH_MEDIUM_IMAGE;
      } else {
        return EMAIL_SIGNATURE_WIDTH;
      }
    }
  }, [emailSignature.images]);

  // if legal disclaimer is the first banner right below the social links, we add extra padding to the top
  const addExtraPaddingTopIfFirstBanner = useMemo(() => {
    if (
      emailSignature.environmentalFooter ||
      emailSignature.banner ||
      showDummyBanner ||
      showDummyEnvironmentalFooter
    ) {
      return false;
    } else {
      return true;
    }
  }, [
    emailSignature.banner,
    emailSignature.environmentalFooter,
    showDummyBanner,
    showDummyEnvironmentalFooter,
  ]);

  useEffect(() => {
    if (!legalDisclaimerNode) return;
    // scroll into view when the disclaimer is first created
    legalDisclaimerNode.scrollIntoView();
  }, [legalDisclaimerNode]);

  return (
    <>
      <tr>
        <td
          style={{
            lineHeight: '0',
            paddingTop: addExtraPaddingTopIfFirstBanner ? '28px' : '15px',
            paddingBottom: '10px',
          }}
        >
          <table cellSpacing="0" cellPadding="0" style={{ width: `${legalWidth}px` }}>
            <tbody>
              <tr>
                <td
                  style={{
                    lineHeight: '0',
                    fontSize: '1pt',
                    borderBottom: '1px solid #acb9be',
                  }}
                >
                  &nbsp;
                </td>
              </tr>
            </tbody>
          </table>
        </td>
      </tr>
      <tr>
        <td
          role={disableAllSpecialEffects ? undefined : 'gridcell'}
          tabIndex={disableAllSpecialEffects ? undefined : 0}
          onKeyDown={(event: React.KeyboardEvent) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              Emitter.emit('SCROLL_TO_LEGAL_DISCLAIMER');
            } else if (event.key === ' ') {
              event.preventDefault();
            }
          }}
          onClick={() => {
            Emitter.emit('SCROLL_TO_LEGAL_DISCLAIMER');
          }}
        >
          <table
            className={
              disableAllSpecialEffects ? undefined : styles['interactive-legal-disclaimer']
            }
            ref={legalDisclaimerNodeRef}
            cellPadding={0}
            cellSpacing={0}
            style={{ width: `${legalWidth}px` }}
          >
            <tbody>
              <tr>
                <td
                  style={{
                    wordBreak: 'break-word',
                    overflowWrap: 'word-break',
                  }}
                >
                  <p
                    style={{
                      color: '#808080',
                      textAlign: 'left',
                      fontSize: `${
                        EMAIL_SIGNATURE_LEGAL_FONT_SIZES_DICT[
                          emailSignature.legalDisclaimer?.fontSize ?? EmailSignatureSizes.SMALL
                        ]
                      }px`,
                      margin: 0,
                      lineHeight: 1.3,
                      fontFamily: emailSignature.design.font,
                    }}
                  >
                    {emailSignature.legalDisclaimer?.value ?? ''}
                  </p>
                </td>
              </tr>
            </tbody>
          </table>
        </td>
      </tr>
    </>
  );
};

type EmailSignatureDummyLegalDisclaimerProps = {
  emailSignature: EmailSignatureV2;
  show: boolean;
};

const EmailSignatureDummyLegalDisclaimer: FC<
  React.PropsWithChildren<EmailSignatureDummyLegalDisclaimerProps>
> = ({ emailSignature, show }) => {
  const [legalDisclaimerNode, legalDisclaimerNodeRef] = useNode<HTMLTableElement>();

  const legalWidth = useMemo(() => {
    const images = emailSignature.images.filter((image) => image.imageUrl);
    if (images.length === 0) {
      return EMAIL_SIGNATURE_WIDTH_WITHOUT_IMAGE;
    } else {
      const biggestImage = sortBy(images, [
        (image) => EM_SIG_IMAGES_SIZES_DICT[image.size] * -1,
      ])[0];
      if (biggestImage.size === 'small') {
        return EMAIL_SIGNATURE_WIDTH_WITH_SMALL_IMAGE;
      } else if (biggestImage.size === 'medium') {
        return EMAIL_SIGNATURE_WIDTH_WITH_MEDIUM_IMAGE;
      } else {
        return EMAIL_SIGNATURE_WIDTH;
      }
    }
  }, [emailSignature.images]);

  // if legal disclaimer is the first banner right below the social links, we add extra padding to the top
  const addExtraPaddingTopIfFirstBanner = useMemo(() => {
    if (emailSignature.environmentalFooter || emailSignature.banner) {
      return false;
    } else {
      return true;
    }
  }, [emailSignature.banner, emailSignature.environmentalFooter]);

  useEffect(() => {
    if (!legalDisclaimerNode) return;
    legalDisclaimerNode.scrollIntoView();
  }, [legalDisclaimerNode]);

  return (
    <AnimatePresence>
      {show && (
        <>
          <tr>
            <td
              style={{
                lineHeight: '0',
                paddingTop: addExtraPaddingTopIfFirstBanner ? '28px' : '15px',
                paddingBottom: '10px',
              }}
            >
              <table cellSpacing="0" cellPadding="0" style={{ width: `${legalWidth}px` }}>
                <tbody>
                  <tr>
                    <td
                      style={{
                        lineHeight: '0',
                        fontSize: '1pt',
                        borderBottom: '1px solid #acb9be',
                      }}
                    >
                      &nbsp;
                    </td>
                  </tr>
                </tbody>
              </table>
            </td>
          </tr>
          <td>
            <td>
              <motion.table
                ref={legalDisclaimerNodeRef}
                cellPadding={0}
                cellSpacing={0}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 1 }}
              >
                <tbody>
                  <tr>
                    <td
                      style={{
                        wordBreak: 'break-word',
                        overflowWrap: 'word-break',
                        width: `${legalWidth}px`,
                      }}
                    >
                      <p
                        style={{
                          color: '#808080',
                          textAlign: 'left',
                          fontSize: `${EMAIL_SIGNATURE_LEGAL_FONT_SIZES_DICT[EmailSignatureSizes.SMALL]}px`,
                          margin: 0,
                          lineHeight: 1.3,
                          fontFamily: emailSignature.design.font,
                        }}
                      >
                        {LEGAL_DISCLAIMER_OPTIONS[0].value}
                      </p>
                    </td>
                  </tr>
                </tbody>
              </motion.table>
            </td>
          </td>
        </>
      )}
    </AnimatePresence>
  );
};

export default EmailSignatureLegalDisclaimer;

export { EmailSignatureDummyLegalDisclaimer };
